import "./assets/index.css";
import { useEffect, useState } from "react";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { DatabaseLogos } from "./components/DatabaseLogos";
import { Features } from "./components/Features";
import { AppPreview } from "./components/AppPreview";
import { Download } from "./components/Download";
import { Pricing } from "./components/Pricing";
import { Footer } from "./components/Footer";
import {
  AnimatedBackground,
  MeshGradient,
} from "./components/AnimatedBackground";
import { AuthProvider } from "./components/AuthProvider";
import { LanguageProvider, useLanguage } from "./i18n";
import { Impressum } from "./pages/Impressum";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { Unsubscribe } from "./pages/Unsubscribe";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Dashboard } from "./pages/Dashboard";
import { Changelog } from "./pages/Changelog";
import { Support } from "./pages/Support";
import { Documentation } from "./pages/Documentation";
import { Purchase } from "./pages/Purchase";
import { PurchaseSuccess } from "./pages/PurchaseSuccess";

type Language = "de" | "en";

function getInitialLanguage(): Language {
  const stored = localStorage.getItem("nexatice-lang");
  if (stored === "de" || stored === "en") return stored;
  if (navigator.language.startsWith("de")) return "de";
  return "en";
}

/**
 * Parses the current pathname into a { lang, page } tuple.
 * Supports both old routes (/impressum) and new prefixed routes (/de/impressum).
 */
function parsePath(pathname: string): { lang: Language; page: string } {
  const match = pathname.match(/^\/(de|en)(\/.*)?$/);
  if (match) {
    return { lang: match[1] as Language, page: match[2] || "/" };
  }
  return { lang: getInitialLanguage(), page: pathname };
}

function Router() {
  const { lang, getPath } = useLanguage();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { page } = parsePath(currentPath);

  // Redirect bare legal routes to prefixed versions
  useEffect(() => {
    const bareLegalRoutes: Record<string, string> = {
      "/impressum": getPath("/impressum"),
      "/privacy": "/en/privacy",
      "/datenschutz": "/de/datenschutz",
      "/terms": "/en/terms",
      "/agb": "/de/agb",
    };

    if (bareLegalRoutes[currentPath]) {
      window.history.replaceState({}, "", bareLegalRoutes[currentPath]);
      setCurrentPath(bareLegalRoutes[currentPath]);
      return;
    }
  }, [currentPath, getPath]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      if (
        anchor &&
        anchor.href &&
        anchor.href.startsWith(window.location.origin)
      ) {
        const url = new URL(anchor.href);

        if (url.pathname === currentPath && url.hash) {
          return;
        }

        if (url.origin !== window.location.origin) {
          return;
        }

        e.preventDefault();
        window.history.pushState({}, "", anchor.href);
        setCurrentPath(url.pathname);
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleClick);
    };
  }, [currentPath]);

  switch (page) {
    case "/impressum":
      return <Impressum />;
    case "/privacy":
    case "/datenschutz":
      return <Privacy />;
    case "/terms":
    case "/agb":
      return <Terms />;
    case "/unsubscribe":
      return <Unsubscribe />;
    case "/login":
      return <Login />;
    case "/register":
      return <Register />;
    case "/forgot-password":
      return <ForgotPassword />;
    case "/reset-password":
      return <ResetPassword />;
    case "/dashboard":
    case "/account":
      return <Dashboard />;
    case "/changelog":
      return <Changelog />;
    case "/support":
      return <Support />;
    case "/docs":
      return <Documentation />;
    case "/purchase":
      return <Purchase />;
    case "/purchase/success":
      return <PurchaseSuccess />;
    default:
      return <HomePage />;
  }
}

// Home page component
function HomePage() {
  return (
    <div className="relative min-h-screen">
      {/* Noise overlay for texture */}
      <div className="noise-overlay" />

      {/* Animated backgrounds */}
      <MeshGradient />
      <AnimatedBackground />

      {/* Navigation */}
      <Navbar currentPage="home" />

      {/* Main Content */}
      <main className="relative z-10">
        <Hero />
        <DatabaseLogos />
        <Features />
        <AppPreview />
        <Pricing />
        <Download />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
